import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Container,
  Paper,
  Alert,
  CircularProgress
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useAuthPasswordResetCreateMutation } from '../../services/GLOWClient';
import { theme } from '../../theme';
import FormInput from '../../SystemDesign/formInput';
import { useTranslation } from 'react-i18next';

const RequestPasswordReset = () => {
  const { t } = useTranslation();

  // Validation schema with translations
  const requestResetSchema = Yup.object().shape({
    email: Yup.string()
      .email(t('request_password_reset.error.invalid_email'))
      .required(t('request_password_reset.error.email_required'))
  });
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [requestPasswordReset, { isLoading }] = useAuthPasswordResetCreateMutation();

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      await requestPasswordReset({
        requestPasswordResetRequest: { email: values.email }
      }).unwrap();
      setSuccess(true);
      setError('');
    } catch (err) {
      console.error('Password reset request failed:', err);
      // Display backend error message (already translated)
      const errorMessage = err.data?.detail || err.data?.error || t('request_password_reset.error.generic');
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const customStyles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      padding: 3,
      backgroundColor: theme.colors.background.primary
    },
    paper: {
      padding: 4,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      maxWidth: 500,
      width: '100%',
      boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
      borderRadius: 2
    },
    title: {
      marginBottom: 3,
      color: theme.colors.primary[500],
      fontWeight: 600
    },
    subtitle: {
      marginBottom: 4,
      textAlign: 'center',
      color: theme.colors.text.secondary
    },
    form: {
      width: '100%',
      marginTop: 2
    },
    textField: {
      marginBottom: 3
    },
    submitButton: {
      marginTop: 2,
      marginBottom: 2,
      padding: '12px 0',
      backgroundColor: theme.colors.primary[500],
      '&:hover': {
        backgroundColor: theme.colors.primary[600]
      }
    },
    backButton: {
      marginTop: 2,
      color: theme.colors.text.secondary,
      '&:hover': {
        backgroundColor: 'transparent',
        color: theme.colors.primary[500]
      }
    },
    successMessage: {
      marginBottom: 3,
      width: '100%'
    }
  };

  return (
    <Container component="main" maxWidth="xs" sx={customStyles.container}>
      <Paper elevation={3} sx={customStyles.paper}>
        <Typography component="h1" variant="h4" sx={customStyles.title}>
          {t('request_password_reset.title')}
        </Typography>
        
        <Typography variant="body1" sx={customStyles.subtitle}>
          {t('request_password_reset.subtitle')}
        </Typography>

        {success ? (
          <Box sx={{ width: '100%' }}>
            <Alert severity="success" sx={customStyles.successMessage}>
              {t('request_password_reset.success_message')}
            </Alert>
            <Button
              fullWidth
              variant="contained"
              sx={customStyles.submitButton}
              onClick={() => navigate('/')}
            >
              {t('request_password_reset.back_to_login')}
            </Button>
          </Box>
        ) : (
          <Formik
            initialValues={{ email: '' }}
            validationSchema={requestResetSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting }) => (
              <Form style={{ width: '100%' }}>
                {error && (
                  <Alert severity="error" sx={{ marginBottom: 3 }}>
                    {error}
                  </Alert>
                )}
                
                <FormInput
                  name="email"
                  label={t('request_password_reset.email_label')}
                  type="email"
                  autoComplete="email"
                  placeholder={t('request_password_reset.email_placeholder')}
                  sx={customStyles.textField}
                  icon={<EmailIcon color="action" />}
                  validateOnChange
                  validateOnBlur
                />
                
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={isSubmitting}
                  sx={customStyles.submitButton}
                >
                  {isSubmitting ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    t('request_password_reset.submit')
                  )}
                </Button>
                
                <Button
                  onClick={() => navigate('/')}
                  fullWidth
                  startIcon={<ArrowBackIcon />}
                  sx={customStyles.backButton}
                >
                  {t('request_password_reset.back_to_login')}
                </Button>
              </Form>
            )}
          </Formik>
        )}
      </Paper>
    </Container>
  );
};

export default RequestPasswordReset;
